import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/// StockAlertAnswer on the server. Result says what happened; the rest only where it does.
export interface StockAlertAnswer {
    Result: 'subscribed' | 'already' | 'in-stock' | 'unavailable' | 'invalid' | 'retry' | 'too-many' | 'failed' | 'cancelled' | 'gone';
    /// Subscribed, and Already to its own account: what cancels it.
    Ticket?: string;
    /// The address the email will go to.
    Email?: string;
    Token?: string;
    RetryAfter?: number;
    Errors?: { [field: string]: string };
}

/// The signed-in shopper's alerts on one product, and the address they go to (null: the account has none usable).
export interface StockAlertsMine {
    SignedIn: boolean;
    Email: string | null;
    Alerts: { OptionId: number | null, Ticket: string, Email: string }[];
    Failed?: boolean;
}

export interface StockAlertRequest {
    ProductId: number;
    OptionId: number | null;
    /// A guest's address; ignored for an account that has one. Nothing else the page sends is text: the server names
    /// the option in the email from the product's own rows.
    Email: string;
    Token: string;
    /// The field people never see; always empty from this page.
    Website: string;
}

/// "Email me when it's back" on a sold-out product (StockAlertController, Databases/Services/StockAlerts): one email when
/// the variant can be bought again in the shopper's country, and the alert is gone after it.
@Injectable({
    providedIn: 'root'
})
export class StockAlertService {
    private readonly api = environment.apiUrl + '/api/StockAlert/';

    constructor(private http: HttpClient) { }

    /// A guest's form token: asked for when the box shows in a browser, sent back with the address.
    token(): Observable<string> {
        return this.http.get<{ Token: string }>(this.api + 'Form').pipe(map(r => r && r.Token));
    }

    mine(productId: number): Observable<StockAlertsMine> {
        return this.http.get<StockAlertsMine>(this.api + 'Mine/' + productId);
    }

    subscribe(request: StockAlertRequest): Observable<StockAlertAnswer> {
        return this.http.post<StockAlertAnswer>(this.api + 'Subscribe', request);
    }

    cancel(ticket: string): Observable<StockAlertAnswer> {
        return this.http.post<StockAlertAnswer>(this.api + 'Cancel', { Ticket: ticket });
    }

    /// Which of the tickets this browser kept still name a waiting alert; Waiting is null when the server could not say.
    check(tickets: string[]): Observable<{ Waiting: string[] | null }> {
        return this.http.post<{ Waiting: string[] | null }>(this.api + 'Check', { Tickets: tickets });
    }
}
